import { FC } from 'react';
import { Col, Divider, Row, Tabs, Typography } from 'antd';
import { TbArrowRight, TbDatabase, TbDatabaseCog, TbDatabasePlus, TbWorldShare } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDataSources } from '@/hooks/useDataSources';
import { SmallTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { PreferenceModalSiderOption } from '@/shared/components/modals/PreferenceModalPrimitives';
import { ConnectedAccount, ConnectedAccountType, SetStateMethod } from '@/types';

interface SiderProps {
    option: string;
    setOption: SetStateMethod<string>;
    view: 'add' | 'manage';
    setView: SetStateMethod<'add' | 'manage'>;
    connectedAccounts: ConnectedAccount[];
}

const Sider: FC<SiderProps> = ({ option, setOption, view, setView, connectedAccounts }): JSX.Element => {
    const { dataSources } = useDataSources();

    const { token } = useAntDesignTheme();

    const all = dataSources.flatMap((section) => (Array.isArray(section.options) ? section.options : []));

    const getCount = (sectionKey: string) => {
        // find the section object first
        const section = dataSources.find((s) => s.key === sectionKey);

        if (!section) {
            return 0;
        }

        if (view === 'add') {
            // Add view: count catalog options in the static section
            return (section?.options?.length ?? 0) as number;
        } else {
            // Manage view: count connected accounts that match any option.key in this section
            return connectedAccounts.filter((acc) =>
                // compare connected account type to each option.key in the section
                (Array.isArray(section.options) ? section.options : []).some(
                    (opt: any) => String(opt.key) === String(acc.type),
                ),
            ).length;
        }
    };

    const getAllCount = view === 'add' ? all.length : connectedAccounts.length;

    const connectedAccountTypes = new Set(connectedAccounts.map((acc) => acc.type));

    const visibleSections = dataSources.filter((section) =>
        section.options.some((option) => connectedAccountTypes.has(option.key as ConnectedAccountType)),
    );

    const filteredDataSources = view === 'add' ? dataSources : visibleSections;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                borderTopLeftRadius: 3,
                borderBottomLeftRadius: 3,
                backgroundColor: token.colorBgBase,
            }}
        >
            <div>
                <div style={{ padding: '1rem', paddingBottom: '0.75rem' }}>
                    <Typography.Title level={5} style={{ marginBottom: '0.25rem' }}>
                        Data Sources
                    </Typography.Title>
                </div>
                <Tabs
                    size='small'
                    activeKey={view}
                    onChange={(v: 'add' | 'manage' | any) => {
                        if (v === 'manage') {
                            setOption('all');
                        }

                        setView(v);
                    }}
                    items={[
                        {
                            key: 'add',
                            label: (
                                <>
                                    <TbDatabasePlus style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                                    Add
                                </>
                            ),
                        },
                        {
                            key: 'manage',
                            label: (
                                <>
                                    <TbDatabaseCog style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                                    Manage
                                    {connectedAccounts?.length !== 0 && (
                                        <SmallTag message={connectedAccounts?.length} />
                                    )}
                                </>
                            ),
                        },
                    ]}
                    tabBarStyle={{
                        marginBottom: '0.5rem',
                        padding: '0 1rem',
                        borderTop: `1px solid ${token.colorBorder}`,
                    }}
                />
                <div style={{ padding: '1rem', paddingTop: 0 }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Categories</small>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <PreferenceModalSiderOption
                            active={option === 'all'}
                            style={{ padding: '0 0.5rem' }}
                            onClick={() => setOption('all')}
                        >
                            <Row style={{ color: token.colorTextSecondary, width: '100%' }} align='middle'>
                                <Col flex='none'>
                                    <TbDatabase
                                        size='1rem'
                                        style={{ marginRight: '0.325rem', verticalAlign: '-0.175rem' }}
                                    />
                                </Col>
                                <Col flex='none'>All Data Sources</Col>
                                <Col flex='auto' />
                                <Col flex='none'>
                                    <SmallTag message={<>{getAllCount}</>} />
                                </Col>
                            </Row>
                        </PreferenceModalSiderOption>
                    </div>
                    <Divider style={{ margin: '1rem 0' }} />
                    {filteredDataSources.map((o) => (
                        <div key={o.key} style={{ marginBottom: '0.75rem' }}>
                            <PreferenceModalSiderOption
                                active={option === o.key}
                                style={{ padding: '0 0.5rem' }}
                                onClick={() => setOption(o.key)}
                            >
                                <Row style={{ color: token.colorTextSecondary, width: '100%' }} align='middle'>
                                    <Col flex='none'>{o.icon}</Col>
                                    <Col flex='none'>{o.label}</Col>
                                    <Col flex='auto' />
                                    <Col flex='none'>
                                        <SmallTag message={<>{getCount(o.key)}</>} />
                                    </Col>
                                </Row>
                            </PreferenceModalSiderOption>
                        </div>
                    ))}
                </div>
            </div>
            {view === 'add' && (
                <div style={{ padding: '1rem' }}>
                    <PlatformAlert
                        title='Need another integration?'
                        status='info'
                        description={
                            <span>
                                Not seeing an integration you need?{' '}
                                <Link to={'/settings/workspace/developers/applications'}>
                                    Build your own
                                    <TbArrowRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                                </Link>{' '}
                                or{' '}
                                <a
                                    href='https://countercyclical.notion.site/2c650036d0868037b1e5e698a7400d00'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    submit an integration request
                                    <TbWorldShare style={{ verticalAlign: '-0.15rem', marginLeft: '0.125rem' }} />
                                </a>{' '}
                                for Countercyclical to build & publish.
                            </span>
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default Sider;
